/**
 * CreativeWorkspacePage — top-level route component for the Creative
 * Workspace at /creative/assets/creative/:creativeId.
 *
 * Phase 0: identity bar + sibling strip wrapping the existing
 * CreativeBundleView. Phase 1 swaps the bundle view for CreativeWorkspaceSplit.
 * Phase 3 enables the Iterate button and adds NextActionsStrip + overlays.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Package } from 'lucide-react';
import { adCatalogApi } from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { CreativeIdentityBar } from './CreativeIdentityBar';
import { CreativeWorkspaceSplit } from './CreativeWorkspaceSplit';
import { SiblingCreativeStrip } from './SiblingCreativeStrip';
import { NextActionsStrip } from './NextActionsStrip';
import { CreativeIterationOverlay } from './CreativeIterationOverlay';
import { BackfillBriefOverlay } from './BackfillBriefOverlay';
import { useUrlState } from './hooks/useUrlState';
import { computePDOFitDelta } from './utils/pdoFitDelta';
import { deriveNextActionCards } from './utils/nextActions';
import { inferBriefSection } from './utils/inferBriefSection';
import type {
  AnalysisTab,
  CreativeSiblingSummary,
  CreativeWorkspaceData,
  IterationPath,
  PDOFitConcept,
  WorkspaceBriefAngle,
} from './types';

export function CreativeWorkspacePage() {
  const navigate = useNavigate();
  const { creativeId } = useParams<{ creativeId: string }>();
  const { clientUid } = useCreativeWorkbench();

  const [data, setData] = useState<CreativeWorkspaceData | null>(null);
  const [siblings, setSiblings] = useState<CreativeSiblingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useUrlState<AnalysisTab>('tab', 'performance');
  const [activePersona, setActivePersona] = useUrlState<string | null>('persona', null);
  const [iterationOpen, setIterationOpen] = useState(false);
  const [iterationPath, setIterationPath] = useState<IterationPath>('ab_variant');
  const [preselectedModIdx, setPreselectedModIdx] = useState<number | undefined>();
  const [backfillOpen, setBackfillOpen] = useState(false);

  useEffect(() => {
    if (!clientUid || !creativeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      adCatalogApi.getCreativeDeepDive(clientUid, creativeId),
      adCatalogApi.getCreativeSiblings(clientUid, creativeId).catch(() => ({
        siblings: [],
        brief_id: null,
        angle_id: null,
      })),
      // Bonnard timeseries — non-blocking; empty series on failure
      adCatalogApi.getCreativeTimeseries(clientUid, creativeId).catch(() => null),
    ])
      .then(([deepDive, siblingsResp, timeseries]) => {
        if (cancelled) return;
        // Merge Bonnard series into the workspace data shape
        const merged: CreativeWorkspaceData = {
          ...deepDive,
          series: timeseries?.series?.length
            ? timeseries.series.map((s) => ({ ...s, ctr: s.ctr ?? undefined }))
            : undefined,
        };
        setData(merged);
        setSiblings(siblingsResp.siblings);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load creative');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientUid, creativeId]);

  // Auto-select the first persona once data lands and there is no URL pin.
  useEffect(() => {
    if (!activePersona && data) {
      const personas = Object.keys(data.review_summaries ?? {});
      if (personas.length > 0) setActivePersona(personas[0]);
    }
  }, [data, activePersona, setActivePersona]);

  // Persona switch: re-fetch the deep-dive with ?persona_id=... so that
  // latest_review (walkthrough, recommendations, intelligence) reflects the
  // active persona's review rather than the default (most recent) analysis.
  // We only merge the persona-specific fields to avoid clobbering layout data.
  const prevPersonaRef = useRef<string | null>(null);
  useEffect(() => {
    if (!clientUid || !creativeId || !activePersona || !data) return;
    if (prevPersonaRef.current === activePersona) return;
    prevPersonaRef.current = activePersona;

    let cancelled = false;
    adCatalogApi
      .getCreativeDeepDive(clientUid, creativeId, activePersona)
      .then((personaDeepDive) => {
        if (cancelled) return;
        setData((prev) =>
          prev
            ? {
                ...prev,
                latest_review: personaDeepDive.latest_review ?? prev.latest_review,
                ontology_bindings: personaDeepDive.ontology_bindings ?? prev.ontology_bindings,
                predictive_impact: personaDeepDive.predictive_impact ?? prev.predictive_impact,
                ranked_brief_mods: personaDeepDive.ranked_brief_mods ?? prev.ranked_brief_mods,
              }
            : personaDeepDive
        );
      })
      .catch(() => {/* non-fatal — keep existing data */});
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePersona]);

  // Resolve the linked brief angle (Phase 2 PDOFitDelta + iteration overlay).
  const linkedAngle = useMemo<WorkspaceBriefAngle | null>(() => {
    if (!data?.brief?.selected_angles) return null;
    const angleId = (data.creative as Record<string, unknown>).angle_id as
      | string
      | undefined
      | null;
    if (!angleId) return null;
    return (
      (data.brief.selected_angles as WorkspaceBriefAngle[]).find(
        (a) => a.angle_id === angleId,
      ) ?? null
    );
  }, [data]);

  const openIterate = (path: IterationPath = 'ab_variant', modIdx?: number) => {
    setIterationPath(path);
    setPreselectedModIdx(modIdx);
    setIterationOpen(true);
  };

  const handleApplyToBrief = (mod: Record<string, unknown>) => {
    if (!data?.brief?.brief_id || !creativeId) return;
    const section = inferBriefSection(mod);
    navigate(
      `/creative/briefs/${data.brief.brief_id}?tab=document&highlight_section=${encodeURIComponent(
        section,
      )}&source_creative=${encodeURIComponent(creativeId)}`,
    );
  };

  const handleBriefLinked = (briefId: string, briefVersion: number, angleId?: string) => {
    setData((prev) => {
      if (!prev) return prev;
      const nextCreative = {
        ...(prev.creative as Record<string, unknown>),
        brief_id: briefId,
        brief_version: briefVersion,
        ...(angleId ? { angle_id: angleId } : {}),
      };
      return {
        ...prev,
        creative: nextCreative,
        brief:
          prev.brief ?? {
            brief_id: briefId,
            title: 'Linked brief',
            status: 'unknown',
            version: briefVersion,
            latest_version: briefVersion,
          },
      };
    });
  };

  // Pull PDO surprise concepts (used by Iterate New Angle path).
  const pdoSurpriseConcepts = useMemo(() => {
    if (!data) return [];
    return computePDOFitDelta(linkedAngle, data.linked_concepts ?? []).filter(
      (c) => c.status === 'surprise',
    );
  }, [data, linkedAngle]);

  const nextActionCards = useMemo(() => {
    if (!data) return [];
    return deriveNextActionCards(data, siblings, {
      openIterate: (modIdx) => openIterate('ab_variant', modIdx),
      openBrief: () => {
        if (data.brief?.brief_id) navigate(`/creative/briefs/${data.brief.brief_id}`);
      },
      openIterateNewAngle: () => openIterate('new_angle'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, siblings]);

  const handleFixGapInBrief = (concept: PDOFitConcept) => {
    if (!data?.brief?.brief_id || !creativeId) return;
    navigate(
      `/creative/briefs/${data.brief.brief_id}?tab=document&gap_concept=${encodeURIComponent(
        concept.concept_id,
      )}&source_creative=${encodeURIComponent(creativeId)}`,
    );
  };

  if (!clientUid) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-10 text-center">
        <Package size={24} className="mx-auto text-slate-400 mb-3" />
        <p className="text-sm text-slate-500">Pick a client in the header to open this creative.</p>
      </div>
    );
  }
  if (!creativeId) return null;
  if (loading && !data) return <LoadingSkeleton />;
  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const creativeRecord = data.creative as Record<string, unknown>;
  const creativeForIdentity = {
    creative_id: (creativeRecord.creative_id as string) ?? creativeId,
    source: creativeRecord.source as string | undefined,
    status: (creativeRecord.status as string) ?? 'draft',
    platform: creativeRecord.platform as string | undefined,
    form:
      (creativeRecord.form as {
        headline?: string;
        media_type?: string;
        thumbnail_url?: string;
        image_url?: string;
      }) ?? {},
    brief_id: (creativeRecord.brief_id as string | null | undefined) ?? null,
    brief_version: (creativeRecord.brief_version as number | null | undefined) ?? null,
    angle_id: (creativeRecord.angle_id as string | null | undefined) ?? null,
  };

  return (
    <div className="flex flex-col min-h-0 -mt-2">
      <CreativeIdentityBar
        creative={creativeForIdentity}
        brief={data.brief ?? null}
        onRunReview={() => {
          /* Phase 1 wires the run-review dropdown */
        }}
        onIterate={() => openIterate()}
        onBackfillBrief={() => setBackfillOpen(true)}
        iterateEnabled
      />

      <div className="flex-1 min-h-0">
        <CreativeWorkspaceSplit
          data={data}
          siblings={siblings}
          siblingStrip={
            <SiblingCreativeStrip siblings={siblings} currentCreativeId={creativeId} />
          }
          activePersona={activePersona}
          onPersonaChange={setActivePersona}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          linkedAngle={linkedAngle}
          onApplyToBrief={handleApplyToBrief}
          onTestVariant={(_mod, idx) => openIterate('ab_variant', idx)}
          onGapClick={handleFixGapInBrief}
        />
      </div>

      <NextActionsStrip cards={nextActionCards} />

      <CreativeIterationOverlay
        isOpen={iterationOpen}
        initialPath={iterationPath}
        preselectedModIndex={preselectedModIdx}
        creative={creativeForIdentity}
        brief={data.brief ?? null}
        rankedMods={data.ranked_brief_mods ?? null}
        siblings={siblings}
        pdoSurpriseConcepts={pdoSurpriseConcepts}
        latestReview={data.latest_review ?? null}
        onClose={() => setIterationOpen(false)}
      />

      <BackfillBriefOverlay
        clientUid={clientUid}
        creativeId={creativeId}
        isOpen={backfillOpen}
        onClose={() => setBackfillOpen(false)}
        onLinked={handleBriefLinked}
      />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-[72px] bg-slate-100 rounded" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 px-4">
        <div className="lg:col-span-2 space-y-3">
          <div className="aspect-square bg-slate-100 rounded-2xl max-w-sm" />
        </div>
        <div className="lg:col-span-3 space-y-3">
          <div className="h-8 bg-slate-100 rounded w-3/4" />
          <div className="h-32 bg-slate-100 rounded-2xl" />
        </div>
      </div>
    </div>
  );
}
